import type { Tweet, TweetReply, InteractionPattern, SuspiciousNetwork, CoordinatedBehavior, ReplyFarm } from "@podx/core/types";
import type { AccountProfile } from "./TokenExtractor";
import { config } from "@podx/core/config";
import { logger } from "@podx/core/logger";

export class BotDetector {
  // Bot detection patterns
  private botPatterns = {
    highFrequency: 50, // tweets per hour threshold
    repetitiveContent: 0.7, // similarity threshold
    urlRatio: 0.8, // URL to text ratio threshold
    followerRatio: 0.1, // following/followers ratio threshold
    accountAge: 30 * 24 * 60 * 60 * 1000, // 30 days in ms
  };

  analyzeAccounts(tweets: Tweet[], accountMap: Map<string, AccountProfile>): void {
    const totalTweets = Array.from(accountMap.values()).reduce((sum, acc) => sum + acc.tweetCount, 0);
    const avgTweetsPerAccount = totalTweets / accountMap.size;

    for (const [username, account] of accountMap) {
      // Calculate engagement rate
      const accountTweets = tweets.filter(t => t.username === username);
      const totalEngagement = accountTweets.reduce((sum, t) => sum + t.likeCount + t.retweetCount, 0);
      account.avgEngagement = accountTweets.length > 0 ? totalEngagement / accountTweets.length : 0;

      // Bot detection algorithm
      account.botScore = this.calculateBotScore(account, accountTweets, avgTweetsPerAccount);

      // Shill detection
      account.shillScore = this.calculateShillScore(account);

      // Reputation assessment
      account.reputation = this.assessReputation(account);
    }
  }

  calculateBotScore(account: AccountProfile, tweets: Tweet[], avgTweetsPerAccount: number): number {
    let botScore = 0;

    // High frequency posting
    const highFrequency = account.tweetCount > avgTweetsPerAccount * 2;
    if (highFrequency) botScore += 0.3;

    // Many tokens posted (potential shilling)
    const manyTokens = account.tokensPosted.length > 5;
    if (manyTokens) botScore += 0.2;

    // Low engagement
    const lowEngagement = account.avgEngagement < 10;
    if (lowEngagement) botScore += 0.2;

    // Repetitive content
    const repetitiveContent = this.detectRepetitiveContent(tweets);
    botScore += repetitiveContent * 0.3;

    return Math.min(1, botScore);
  }

  calculateShillScore(account: AccountProfile): number {
    let shillScore = 0;

    // Many different tokens posted quickly
    const manyTokens = account.tokensPosted.length > 3;
    if (manyTokens) shillScore += 0.4;

    // Low account age (new accounts shilling)
    // This would need account creation date data

    // Pattern of promoting multiple tokens
    const tokenDiversity = account.tokensPosted.length / Math.max(account.tweetCount, 1);
    if (tokenDiversity > 0.5) shillScore += 0.3;

    return Math.min(1, shillScore);
  }

  detectRepetitiveContent(tweets: Tweet[]): number {
    if (tweets.length < 2) return 0;

    let similarityScore = 0;
    const texts = tweets.map(t => t.text.toLowerCase());

    for (let i = 0; i < texts.length - 1; i++) {
      for (let j = i + 1; j < texts.length; j++) {
        const a = texts[i];
        const b = texts[j];
        if (a !== undefined && b !== undefined) {
          const similarity = this.calculateTextSimilarity(a, b);
          similarityScore += similarity;
        }
      }
    }

    const avgSimilarity = similarityScore / (texts.length * (texts.length - 1) / 2);
    return Math.min(1, avgSimilarity);
  }

  calculateTextSimilarity(text1: string, text2: string): number {
    const words1 = text1.split(/\s+/);
    const words2 = text2.split(/\s+/);

    const intersection = words1.filter(word => words2.includes(word));
    const union = [...new Set([...words1, ...words2])];

    return intersection.length / union.length;
  }

  assessReputation(account: AccountProfile): 'trusted' | 'suspicious' | 'bot' {
    if (account.botScore > 0.7) return 'bot';
    if (account.shillScore > 0.6 || account.botScore > 0.4) return 'suspicious';
    return 'trusted';
  }

  calculateTokenBotScore(tweets: Tweet[], accountMap: Map<string, AccountProfile>): number {
    const usernames = tweets.map(t => t.username).filter((username): username is string => username !== undefined);
    const uniqueUsernames = [...new Set(usernames)];

    let totalBotScore = 0;
    for (const username of uniqueUsernames) {
      const account = accountMap.get(username);
      if (account) {
        totalBotScore += account.botScore;
      }
    }

    return totalBotScore / uniqueUsernames.length;
  }

  calculateShillProbability(tweets: Tweet[], accountMap: Map<string, AccountProfile>, symbol: string): number {
    const usernames = tweets.map(t => t.username).filter((username): username is string => username !== undefined);
    const uniqueUsernames = [...new Set(usernames)];

    let totalShillScore = 0;
    let suspiciousAccounts = 0;

    for (const username of uniqueUsernames) {
      const account = accountMap.get(username);
      if (account) {
        totalShillScore += account.shillScore;
        if (account.reputation === 'suspicious' || account.reputation === 'bot') {
          suspiciousAccounts++;
        }
      }
    }

    const avgShillScore = totalShillScore / uniqueUsernames.length;
    const suspiciousRatio = suspiciousAccounts / uniqueUsernames.length;

    return (avgShillScore + suspiciousRatio) / 2;
  }

  // Enhanced social graph analysis
  analyzeInteractionPatterns(replies: TweetReply[]): {
    suspiciousNetworks: SuspiciousNetwork[];
    coordinatedBehavior: CoordinatedBehavior[];
    replyFarms: ReplyFarm[];
  } {
    logger.info('Starting interaction pattern analysis', {
      operation: 'interaction_analysis',
      replyCount: replies.length
    });

    if (replies.length < config.botDetection.minRepliesForAnalysis) {
      logger.debug('Insufficient replies for analysis', {
        operation: 'interaction_analysis',
        replyCount: replies.length,
        required: config.botDetection.minRepliesForAnalysis
      });
      
      return {
        suspiciousNetworks: [],
        coordinatedBehavior: [],
        replyFarms: []
      };
    }

    const interactions = this.buildInteractionGraph(replies);
    
    return {
      suspiciousNetworks: this.detectSuspiciousNetworks(interactions),
      coordinatedBehavior: this.detectCoordinatedBehavior(replies),
      replyFarms: this.detectReplyFarms(interactions, replies)
    };
  }

  private buildInteractionGraph(replies: TweetReply[]): Map<string, InteractionPattern[]> {
    const graph = new Map<string, InteractionPattern[]>();
    
    // Group replies by user to analyze patterns
    const userReplies = new Map<string, TweetReply[]>();
    for (const reply of replies) {
      if (!userReplies.has(reply.username)) {
        userReplies.set(reply.username, []);
      }
      userReplies.get(reply.username)!.push(reply);
    }
    
    // Analyze each user's reply patterns
    for (const [username, userReplyList] of userReplies) {
      const patterns = this.analyzeUserInteractions(userReplyList);
      graph.set(username, patterns);
    }
    
    logger.debug('Built interaction graph', {
      operation: 'build_interaction_graph',
      userCount: graph.size,
      totalInteractions: Array.from(graph.values()).reduce((sum, patterns) => sum + patterns.length, 0)
    });
    
    return graph;
  }

  private analyzeUserInteractions(replies: TweetReply[]): InteractionPattern[] {
    // Track who this user replies to most often
    const targetFrequency = new Map<string, number>();
    const targetTimes = new Map<string, Date[]>();
    
    for (const reply of replies) {
      // Extract who they're replying to (would need parent tweet data or mentions)
      const targetUser = this.extractTargetUser(reply);
      if (targetUser) {
        targetFrequency.set(targetUser, (targetFrequency.get(targetUser) || 0) + 1);
        if (!targetTimes.has(targetUser)) {
          targetTimes.set(targetUser, []);
        }
        targetTimes.get(targetUser)!.push(new Date(reply.createdAt));
      }
    }
    
    const patterns: InteractionPattern[] = [];
    for (const [targetUser, frequency] of targetFrequency) {
      const times = targetTimes.get(targetUser) || [];
      const timePattern = this.analyzeTimePattern(times);
      const suspicionLevel = this.calculateInteractionSuspicion(frequency, timePattern, replies.length);
      
      patterns.push({
        sourceUser: replies[0]?.username || '',
        targetUser,
        interactionType: 'reply',
        frequency,
        timePattern,
        suspicionLevel
      });
    }
    
    return patterns;
  }

  private extractTargetUser(reply: TweetReply): string | null {
    // Extract @mentions from reply text as proxy for reply target
    const mentions = reply.text.match(/@(\w+)/g);
    return mentions?.[0]?.substring(1) || null;
  }

  private analyzeTimePattern(times: Date[]): 'consistent' | 'burst' | 'random' {
    if (times.length < 2) return 'random';
    
    times.sort((a, b) => a.getTime() - b.getTime());
    const intervals = [];
    
    for (let i = 1; i < times.length; i++) {
      intervals.push(times[i].getTime() - times[i-1].getTime());
    }
    
    const avgInterval = intervals.reduce((sum, interval) => sum + interval, 0) / intervals.length;
    const variance = intervals.reduce((sum, interval) => sum + Math.pow(interval - avgInterval, 2), 0) / intervals.length;
    const stdDev = Math.sqrt(variance);
    
    // If standard deviation is low compared to average, it's consistent
    if (stdDev < avgInterval * 0.3) return 'consistent';
    
    // If many replies in short bursts, it's burst pattern
    const shortIntervals = intervals.filter(interval => interval < 60000).length; // < 1 minute
    if (shortIntervals > intervals.length * 0.7) return 'burst';
    
    return 'random';
  }

  private calculateInteractionSuspicion(frequency: number, timePattern: 'consistent' | 'burst' | 'random', totalReplies: number): number {
    let suspicion = 0;
    
    // High frequency with same target is suspicious
    const targetRatio = frequency / totalReplies;
    if (targetRatio > 0.5) suspicion += 0.4;
    else if (targetRatio > 0.3) suspicion += 0.2;
    
    // Consistent timing is bot-like
    if (timePattern === 'consistent') suspicion += 0.3;
    else if (timePattern === 'burst') suspicion += 0.2;
    
    // Very high frequency overall is suspicious
    if (frequency > 10) suspicion += 0.3;
    
    return Math.min(1, suspicion);
  }

  private detectSuspiciousNetworks(interactions: Map<string, InteractionPattern[]>): SuspiciousNetwork[] {
    const networks: SuspiciousNetwork[] = [];
    const { botDetection } = config;
    
    // Group accounts by interaction targets
    const targetGroups = new Map<string, string[]>();
    
    for (const [username, patterns] of interactions) {
      for (const pattern of patterns) {
        if (pattern.suspicionLevel > 0.5) {
          const key = pattern.targetUser;
          if (!targetGroups.has(key)) {
            targetGroups.set(key, []);
          }
          targetGroups.get(key)!.push(username);
        }
      }
    }
    
    // Identify potential networks
    for (const [targetUser, accounts] of targetGroups) {
      if (accounts.length >= botDetection.replyFarmMinAccounts) {
        const confidence = Math.min(1, accounts.length / 10); // More accounts = higher confidence
        
        if (confidence >= botDetection.suspiciousNetworkConfidence) {
          networks.push({
            accounts,
            networkType: 'reply_farm',
            confidence,
            evidence: [
              `${accounts.length} accounts repeatedly targeting @${targetUser}`,
              'Coordinated reply patterns detected'
            ],
            timeframe: {
              start: new Date(Date.now() - botDetection.timeWindowMinutes * 60 * 1000),
              end: new Date()
            }
          });
        }
      }
    }
    
    logger.debug('Detected suspicious networks', {
      operation: 'detect_suspicious_networks',
      networkCount: networks.length
    });
    
    return networks;
  }

  private detectCoordinatedBehavior(replies: TweetReply[]): CoordinatedBehavior[] {
    const behaviors: CoordinatedBehavior[] = [];
    const { botDetection } = config;
    
    // 1. Detect simultaneous replies (same minute posting)
    const simultaneousBehavior = this.detectSimultaneousPosting(replies);
    if (simultaneousBehavior) behaviors.push(simultaneousBehavior);
    
    // 2. Detect template spreading
    const templateBehavior = this.detectTemplateSpreading(replies);
    if (templateBehavior) behaviors.push(templateBehavior);
    
    logger.debug('Detected coordinated behaviors', {
      operation: 'detect_coordinated_behavior',
      behaviorCount: behaviors.length
    });
    
    return behaviors;
  }

  private detectSimultaneousPosting(replies: TweetReply[]): CoordinatedBehavior | null {
    const { botDetection } = config;
    
    // Group replies by minute timestamps
    const timeGroups = new Map<string, TweetReply[]>();
    
    for (const reply of replies) {
      const minute = new Date(reply.createdAt).toISOString().substring(0, 16); // YYYY-MM-DDTHH:MM
      if (!timeGroups.has(minute)) {
        timeGroups.set(minute, []);
      }
      timeGroups.get(minute)!.push(reply);
    }
    
    // Find suspicious simultaneous activity
    for (const [minute, groupReplies] of timeGroups) {
      if (groupReplies.length >= botDetection.replyFarmMinAccounts) {
        const uniqueUsers = new Set(groupReplies.map(r => r.username)).size;
        if (uniqueUsers === groupReplies.length) { // All different users
          const suspiciousScore = Math.min(1, groupReplies.length / 5);
          
          if (suspiciousScore >= botDetection.coordinatedActivityThreshold) {
            return {
              type: 'simultaneous_replies',
              participants: groupReplies.map(r => r.username),
              pattern: `${groupReplies.length} replies in minute ${minute}`,
              frequency: groupReplies.length,
              suspiciousScore
            };
          }
        }
      }
    }
    
    return null;
  }

  private detectTemplateSpreading(replies: TweetReply[]): CoordinatedBehavior | null {
    const { botDetection } = config;
    const similarityGroups = new Map<string, TweetReply[]>();
    
    // Find replies with high similarity scores
    for (let i = 0; i < replies.length; i++) {
      for (let j = i + 1; j < replies.length; j++) {
        const similarity = this.calculateTextSimilarity(replies[i].text, replies[j].text);
        if (similarity > botDetection.similarityThreshold && replies[i].username !== replies[j].username) {
          const key = `${Math.min(i, j)}-${Math.max(i, j)}`;
          if (!similarityGroups.has(key)) {
            similarityGroups.set(key, [replies[i], replies[j]]);
          }
        }
      }
    }
    
    if (similarityGroups.size > 0) {
      const largestGroup = Array.from(similarityGroups.values())
        .reduce((largest, current) => current.length > largest.length ? current : largest);
      
      const suspiciousScore = Math.min(1, largestGroup.length / 3);
      
      if (suspiciousScore >= botDetection.coordinatedActivityThreshold) {
        return {
          type: 'template_spreading',
          participants: largestGroup.map(r => r.username),
          pattern: 'Nearly identical reply content',
          frequency: largestGroup.length,
          suspiciousScore
        };
      }
    }
    
    return null;
  }

  private detectReplyFarms(interactions: Map<string, InteractionPattern[]>, replies: TweetReply[]): ReplyFarm[] {
    const farms: ReplyFarm[] = [];
    const { botDetection } = config;
    
    // Group accounts by behavior patterns
    const behaviorGroups = this.groupAccountsByBehavior(interactions);
    
    for (const group of behaviorGroups) {
      if (group.length >= botDetection.replyFarmMinAccounts) {
        const farm = this.analyzePotentialFarm(group, replies);
        if (farm && farm.riskScore > 0.6) {
          farms.push(farm);
        }
      }
    }
    
    logger.debug('Detected reply farms', {
      operation: 'detect_reply_farms',
      farmCount: farms.length
    });
    
    return farms.sort((a, b) => b.riskScore - a.riskScore);
  }

  private groupAccountsByBehavior(interactions: Map<string, InteractionPattern[]>): string[][] {
    const accounts = Array.from(interactions.keys());
    const behaviorGroups: string[][] = [];
    
    for (const account of accounts) {
      const patterns = interactions.get(account) || [];
      let addedToGroup = false;
      
      // Try to add to existing group based on similar patterns
      for (const group of behaviorGroups) {
        const representativeAccount = group[0];
        const repPatterns = interactions.get(representativeAccount) || [];
        
        if (this.haveSimilarBehavior(patterns, repPatterns)) {
          group.push(account);
          addedToGroup = true;
          break;
        }
      }
      
      // Create new group if no similar behavior found
      if (!addedToGroup) {
        behaviorGroups.push([account]);
      }
    }
    
    return behaviorGroups;
  }

  private haveSimilarBehavior(patterns1: InteractionPattern[], patterns2: InteractionPattern[]): boolean {
    // Check if accounts have similar target preferences and timing
    const targets1 = new Set(patterns1.map(p => p.targetUser));
    const targets2 = new Set(patterns2.map(p => p.targetUser));
    
    const commonTargets = new Set([...targets1].filter(x => targets2.has(x)));
    const unionTargets = new Set([...targets1, ...targets2]);
    
    const targetSimilarity = commonTargets.size / Math.max(unionTargets.size, 1);
    
    return targetSimilarity > 0.5; // 50% target overlap indicates similar behavior
  }

  private analyzePotentialFarm(accounts: string[], replies: TweetReply[]): ReplyFarm | null {
    const farmReplies = replies.filter(r => accounts.includes(r.username));
    const totalReplies = farmReplies.length;
    
    if (totalReplies === 0) return null;
    
    // Calculate farm metrics
    const replyFrequency = totalReplies / accounts.length;
    const targetAccounts = [...new Set(farmReplies.map(r => this.extractTargetUser(r)).filter(Boolean))];
    
    // Analyze content patterns for genericity
    const genericReplies = farmReplies.filter(r => this.isGenericContent(r.text)).length;
    const genericContentRatio = genericReplies / totalReplies;
    
    // Analyze timing patterns
    const timePatternSuspicion = this.analyzeGroupTimePatterns(farmReplies);
    
    // Calculate account similarity
    const accountSimilarity = this.calculateAccountSimilarity(accounts, farmReplies);
    
    const riskScore = Math.min(1,
      (replyFrequency > 10 ? 0.25 : replyFrequency / 40) +
      (genericContentRatio * 0.3) +
      (timePatternSuspicion * 0.25) +
      (accountSimilarity * 0.2)
    );
    
    if (riskScore > 0.6) {
      return {
        accounts,
        targetAccounts,
        farmType: genericContentRatio > 0.7 ? 'engagement_farming' : 
                  replyFrequency > 15 ? 'spam_amplification' : 'reputation_boosting',
        evidence: {
          replyFrequency,
          genericContentRatio,
          timePatternSuspicion,
          accountSimilarity
        },
        riskScore
      };
    }
    
    return null;
  }

  private isGenericContent(text: string): boolean {
    const genericPatterns = [
      /^(nice|great|awesome|good|cool)\s*!*$/i,
      /^(thanks?|thx)\s*!*$/i,
      /^(yes|no|true|false)\s*!*$/i,
      /^(lol|haha|hehe)\s*!*$/i,
      /^(first|second|third)\s*!*$/i,
    ];
    
    return genericPatterns.some(pattern => pattern.test(text.trim()));
  }

  private analyzeGroupTimePatterns(replies: TweetReply[]): number {
    if (replies.length < 2) return 0;
    
    const times = replies.map(r => new Date(r.createdAt).getTime()).sort();
    let simultaneousCount = 0;
    
    // Check for replies posted within 1 minute of each other
    for (let i = 1; i < times.length; i++) {
      if (times[i] - times[i-1] < 60000) { // 1 minute
        simultaneousCount++;
      }
    }
    
    return simultaneousCount / Math.max(times.length - 1, 1);
  }

  private calculateAccountSimilarity(accounts: string[], replies: TweetReply[]): number {
    // Simple heuristic: similar username patterns, posting frequency
    const patterns = accounts.map(username => {
      const userReplies = replies.filter(r => r.username === username);
      return {
        usernameLength: username.length,
        hasNumbers: /\d/.test(username),
        hasUnderscores: /_/.test(username),
        replyCount: userReplies.length,
        avgLength: userReplies.reduce((sum, r) => sum + r.text.length, 0) / Math.max(userReplies.length, 1)
      };
    });
    
    // Calculate similarity score based on pattern consistency
    let similarityScore = 0;
    const avgReplyCount = patterns.reduce((sum, p) => sum + p.replyCount, 0) / patterns.length;
    
    // Similar reply counts
    const replyCountSimilarity = patterns.filter(p => Math.abs(p.replyCount - avgReplyCount) < 2).length / patterns.length;
    similarityScore += replyCountSimilarity * 0.5;
    
    // Similar username patterns
    const hasNumbersRatio = patterns.filter(p => p.hasNumbers).length / patterns.length;
    const hasUnderscoresRatio = patterns.filter(p => p.hasUnderscores).length / patterns.length;
    
    if (hasNumbersRatio > 0.7 || hasUnderscoresRatio > 0.7) {
      similarityScore += 0.3;
    }
    
    return Math.min(1, similarityScore);
  }
}
